import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PhoneInput from '../components/PhoneInput';

const RegisterPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await register(email, password, name, phone);
    
    if (result.success) {
      toast.success(t('toast.accountCreated'));
      navigate('/');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('auth.createAccount')}
          </h1>
          <p className="text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('auth.joinCommunity')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700">{t('common.name')}</Label>
              <Input
                id="name"
                data-testid="register-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1"
                placeholder="John Doe"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">{t('common.email')}</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-700">{t('common.phone')}</Label>
              <PhoneInput
                value={phone}
                onChange={setPhone}
                required
                className="mt-1"
                testId="register-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">{t('common.password')}</Label>
              <Input
                id="password"
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              data-testid="register-submit-btn"
              disabled={loading}
              className="w-full bg-blue-900 text-white hover:bg-blue-800"
            >
              {loading ? t('auth.creatingAccount') : t('auth.createAccountButton')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="text-blue-900 font-bold hover:text-blue-800">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
            ← {t('common.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
